import React, { useState } from 'react'
import { toast } from 'sonner'
import { useQueryClient } from 'react-query'
import { format } from 'date-fns'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { timeEntryService } from '@/services/timeEntryService'

const hasOverlap = (newStart, newEnd, entries, excludeId) => {
  return entries
    .filter(e => e.id !== excludeId && e.end_time)
    .some(e => {
      const s = new Date(e.start_time)
      const en = new Date(e.end_time)
      return newStart < en && s < newEnd
    })
}

const EditTimeEntryForm = ({ entry, taskId, projectId, onClose, existingEntries = [] }) => {
  const queryClient = useQueryClient()
  const [form, setForm] = useState({
    start_time: format(new Date(entry.start_time), "yyyy-MM-dd'T'HH:mm"),
    end_time: format(new Date(entry.end_time), "yyyy-MM-dd'T'HH:mm"),
    note: entry.note || ''
  })
  const [loading, setLoading] = useState(false)
  const nowMax = format(new Date(), "yyyy-MM-dd'T'HH:mm")

  const handleSubmit = async (e) => {
    e.preventDefault()
    const currentNow = new Date()
    if (!form.start_time || !form.end_time) return toast.error('Start and end time are required')
    if (new Date(form.start_time) > currentNow) return toast.error('Start time cannot be in the future')
    if (new Date(form.end_time) > currentNow) return toast.error('End time cannot be in the future')
    if (new Date(form.end_time) <= new Date(form.start_time)) return toast.error('End time must be after start time')
    if (hasOverlap(new Date(form.start_time), new Date(form.end_time), existingEntries, entry.id))
      return toast.error('This time range overlaps with an existing entry for this task')

    setLoading(true)
    try {
      await timeEntryService.update(entry.id, {
        start_time: new Date(form.start_time).toISOString(),
        end_time: new Date(form.end_time).toISOString(),
        note: form.note.trim() || null
      })
      queryClient.invalidateQueries(['projects', String(projectId), 'tasks', String(taskId)])
      toast.success('Time entry updated')
      onClose?.()
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update time entry')
    } finally {
      setLoading(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3 p-3 border rounded-lg bg-muted/30">
      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-1">
          <Label className="text-xs">Start</Label>
          <Input
            type="datetime-local"
            value={form.start_time}
            max={nowMax}
            onChange={(e) => setForm(f => ({ ...f, start_time: e.target.value }))}
            className="h-8 text-xs"
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">End</Label>
          <Input
            type="datetime-local"
            value={form.end_time}
            max={nowMax}
            onChange={(e) => setForm(f => ({ ...f, end_time: e.target.value }))}
            className="h-8 text-xs"
          />
        </div>
      </div>
      <div className="space-y-1">
        <Label className="text-xs">Note (optional)</Label>
        <Textarea
          value={form.note}
          onChange={(e) => setForm(f => ({ ...f, note: e.target.value }))}
          placeholder="What did you work on?"
          rows={2}
          className="text-sm resize-none"
        />
      </div>
      <div className="flex gap-2">
        <Button type="submit" size="sm" className="h-7 text-xs" disabled={loading}>
          {loading ? 'Saving...' : 'Save Changes'}
        </Button>
        <Button type="button" size="sm" variant="ghost" className="h-7 text-xs" onClick={() => onClose?.()}>
          Cancel
        </Button>
      </div>
    </form>
  )
}

export default EditTimeEntryForm
